if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.isDocked = false;
      this.dockMarker = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.section = document.getElementById(`shopify-section-${this.sectionId}`);
      this.productForm = this.section.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
      
      let lastY = 0;

      // Observer for showing/hiding based on product form visibility
      this.observer = new IntersectionObserver(([entry]) => {
        const currentY = entry.boundingClientRect.y;
        const scrollingDown = currentY < lastY;
        lastY = currentY;

        if (!entry.isIntersecting && scrollingDown) {
          this.classList.add('sticky-atc--active');
        } else {
          this.classList.remove('sticky-atc--active');
        }
      });

      this.observer.observe(this.productForm);
      
      // Find the dock marker (Read More button or last info block)
      this.findDockMarker();
      
      // Setup dock observer
      this.setupDockObserver();
      
      // Initial position
      this.updatePosition();
      
      // Update on resize
      this.resizeHandler = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.isDocked = false;
          this.style.position = '';
          this.style.top = '';
          this.style.bottom = '';
          this.style.left = '';
          this.style.width = '';
          this.updatePosition();
          // Re-setup dock observer
          if (this.dockObserver) this.dockObserver.disconnect();
          this.setupDockObserver();
        }, 150);
      };
      window.addEventListener('resize', this.resizeHandler);
      
      // Scroll handler for left/width updates only (not position)
      this.scrollHandler = () => {
        if (this.scrollRAF) return;
        this.scrollRAF = requestAnimationFrame(() => {
          this.updateLeftWidth();
          this.scrollRAF = null;
        });
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
    
    findDockMarker() {
      // Look for the Read More button block
      const blocks = document.querySelectorAll('.pdp__content .shopify-block.section');
      for (const block of blocks) {
        if (block.innerText.includes('Read More')) {
          this.dockMarker = block;
          console.log('[StickyATC] Found dock marker: Read More block');
          return;
        }
      }
      
      // Fallback: use the content wrapper
      this.dockMarker = document.querySelector('.pdp__content-wrapper');
      console.log('[StickyATC] Using fallback dock marker: content wrapper');
    }
    
    setupDockObserver() {
      if (!this.dockMarker || window.innerWidth < 1024) return;
      
      // Watch the dock marker
      // When its bottom edge crosses the viewport bottom, dock the ATC
      this.dockObserver = new IntersectionObserver((entries) => {
        if (window.innerWidth < 1024) return;
        
        const entry = entries[0];
        const markerRect = this.dockMarker.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const stickyHeight = this.offsetHeight || 65;
        
        // Marker bottom is above (viewport bottom - ATC height) = time to dock
        const dockThreshold = viewportHeight - stickyHeight;
        
        if (markerRect.bottom < dockThreshold && markerRect.bottom > -500) {
          // Dock: marker is above where ATC would sit
          if (!this.isDocked) {
            this.dock();
          }
        } else if (markerRect.bottom >= dockThreshold) {
          // Undock: marker is below dock threshold (scrolling back up)
          if (this.isDocked) {
            this.undock();
          }
        }
      }, {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
      });
      
      this.dockObserver.observe(this.dockMarker);
      
      // Also check on scroll for more precise control
      this.dockScrollHandler = () => {
        if (this.dockScrollRAF) return;
        this.dockScrollRAF = requestAnimationFrame(() => {
          this.checkDockState();
          this.dockScrollRAF = null;
        });
      };
      window.addEventListener('scroll', this.dockScrollHandler, { passive: true });
    }
    
    checkDockState() {
      if (window.innerWidth < 1024 || !this.dockMarker) return;
      
      const markerRect = this.dockMarker.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const stickyHeight = this.offsetHeight || 65;
      const dockThreshold = viewportHeight - stickyHeight;
      
      if (markerRect.bottom < dockThreshold && markerRect.bottom > -500) {
        if (!this.isDocked) this.dock();
      } else if (markerRect.bottom >= dockThreshold) {
        if (this.isDocked) this.undock();
      }
    }
    
    dock() {
      console.log('[StickyATC] DOCKING');
      this.isDocked = true;
      
      const markerRect = this.dockMarker.getBoundingClientRect();
      const stickyHeight = this.offsetHeight || 65;
      
      // Calculate absolute position: marker bottom + scroll position
      const absoluteTop = markerRect.bottom + window.scrollY;
      
      // Get left/width from inner block
      const innerBlock = document.querySelector('.pdp__content .shopify-block.section');
      let targetLeft, targetWidth;
      if (innerBlock) {
        const innerRect = innerBlock.getBoundingClientRect();
        targetLeft = innerRect.left + window.scrollX;
        targetWidth = innerRect.width;
      } else {
        const infoPanel = document.querySelector('.pdp__content');
        const infoRect = infoPanel.getBoundingClientRect();
        targetLeft = infoRect.left + window.scrollX;
        targetWidth = infoRect.width;
      }
      
      // Apply absolute positioning
      this.style.position = 'absolute';
      this.style.top = `${absoluteTop}px`;
      this.style.bottom = 'auto';
      this.style.left = `${targetLeft}px`;
      this.style.width = `${targetWidth}px`;
    }
    
    undock() {
      console.log('[StickyATC] UNDOCKING');
      this.isDocked = false;
      this.updatePosition();
    }
    
    updatePosition() {
      // Mobile/tablet: full width fixed at bottom
      if (window.innerWidth < 1024) {
        this.style.position = 'fixed';
        this.style.left = '0';
        this.style.width = '100%';
        this.style.bottom = '0';
        this.style.top = 'auto';
        return;
      }
      
      // Desktop: fixed at viewport bottom, aligned to column
      if (this.isDocked) return; // Don't change if docked
      
      const innerBlock = document.querySelector('.pdp__content .shopify-block.section');
      let targetLeft, targetWidth;
      if (innerBlock) {
        const innerRect = innerBlock.getBoundingClientRect();
        targetLeft = innerRect.left;
        targetWidth = innerRect.width;
      } else {
        const infoPanel = document.querySelector('.pdp__content');
        if (!infoPanel) return;
        const infoRect = infoPanel.getBoundingClientRect();
        targetLeft = infoRect.left;
        targetWidth = infoRect.width;
      }
      
      this.style.position = 'fixed';
      this.style.bottom = '0';
      this.style.top = 'auto';
      this.style.left = `${targetLeft}px`;
      this.style.width = `${targetWidth}px`;
    }
    
    updateLeftWidth() {
      // Keep left/width aligned during scroll (for fixed positioning)
      if (window.innerWidth < 1024 || this.isDocked) return;
      
      const innerBlock = document.querySelector('.pdp__content .shopify-block.section');
      if (innerBlock) {
        const innerRect = innerBlock.getBoundingClientRect();
        this.style.left = `${innerRect.left}px`;
        this.style.width = `${innerRect.width}px`;
      }
    }
    
    disconnectedCallback() {
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.dockScrollHandler) window.removeEventListener('scroll', this.dockScrollHandler);
      if (this.observer) this.observer.disconnect();
      if (this.dockObserver) this.dockObserver.disconnect();
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}